// src/utils/temperatureStore.js
// =====================  WHERE THIS GOES  =====================
// New file. Twin of windStore.js / sunPathStore.js. Bridges the right-pane
// TemperaturePanel and the Three.js temperature controller in Site3DView.
// Panel writes params + reads results/status/climate; Site3DView registers
// the scene controller. Neither imports the other. Kept as a SEPARATE store
// (not merged into a shared climate store) to mirror the proven pattern 1:1.
//
// IMPORTANT: registerScene / clearScene / setStatus collide by name with
// sunPathStore + windStore — Site3DView must import them ALIASED
// (registerTemperatureScene, clearTemperatureScene, setTemperatureStatus).
// ============================================================

import { useEffect, useReducer } from 'react';

const state = {
  params: {
    month: 4,                    // May — a hot month by default
    manual: true,                // true until /api/climate is fetched/applied
    clearness: 1.0,              // manual clear-sky knob (1 = clear)
    albedo: 0.2,
    lat: 22.31, lng: 73.18,
    // gain knobs
    shgc: 0.5, absorptance: 0.6, tIn: 25,
    // visibility toggles
    showFacade: true, showRoof: true, showUHI: false,
  },
  climate: null,   // { source, perMonth:[{month,ghi,dni,dhi,tMean,tMax,tMin,rh}], annual }
  results: null,   // scene analyze() payload (perCard, roof, geom, uhi, hi/lo, roofYear())
  status: { hasMassing: false, hasContext: false },
};

let updater = null, analyzer = null, activator = null;
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());

export const getParams = () => state.params;
export const getResults = () => state.results;
export const getStatus = () => state.status;
export const getClimate = () => state.climate;

export function setParams(patch) {
  state.params = { ...state.params, ...patch };
  // keep the scene's cached solar in sync so integrate() can read it
  state.params.cachedSolar = state.climate ? { perMonth: state.climate.perMonth } : null;
  if (updater) { try { updater(state.params); } catch (e) { console.warn('[temp] update', e); } }
  emit();
}
export function setResults(r) { state.results = r; emit(); }
export function setStatus(s) { state.status = { ...state.status, ...s }; emit(); }
export function setClimate(c) {
  state.climate = c;
  state.params.cachedSolar = c ? { perMonth: c.perMonth } : null;
  emit();
}

// Site3DView calls this once its controller exists.
export function registerScene({ update, analyze, setActive }) {
  updater = update || null; analyzer = analyze || null; activator = setActive || null;
}
export function clearScene() { updater = analyzer = activator = null; }

export function activate() { if (activator) activator(true); if (updater) updater(state.params); }
export function deactivate() { if (activator) activator(false); }

export function runAnalysis() {
  if (!analyzer) return null;
  const r = analyzer(state.params);
  setResults(r);
  return r;
}

export function useTemperature() {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => { listeners.add(force); return () => listeners.delete(force); }, []);
  return {
    params: state.params, results: state.results, status: state.status, climate: state.climate,
    setParams, setClimate, runAnalysis, activate, deactivate,
    hasScene: !!analyzer,
  };
}
